import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { SalespersonDTO, UserContextSalespersonService, UserContextType, UserDTO, UsersService } from '@api';
import { lastValueFrom } from 'rxjs';

interface UserState {
  user: UserDTO | undefined;
  isLoading: boolean;
  error: string | null;
}

const initialState: UserState = {
  user: undefined,
  isLoading: false,
  error: null,
};

export const UserStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withMethods((store) => {
    const userService = inject(UsersService);
    const userContextSalespersonService = inject(UserContextSalespersonService);

    return {
      async fetchUserInformation(): Promise<UserDTO> {
        if (store.user()) return Promise.resolve(store.user()!);
        patchState(store, () => ({
          isLoading: true,
          error: null,
        }));

        return new Promise((resolve, reject) => {
          userService.getCurrentUser().subscribe({
            next: (user: UserDTO) => {
              patchState(store, () => ({
                user: user,
                isLoading: false,
              }));
              resolve(user);
            },
            error: (err) => {
              console.error('Failed to fetch user information: ', err?.message);
              patchState(store, () => ({
                isLoading: false,
                error: err?.message || 'Failed to fetch user.',
              }));
              reject(err);
            },
          });
        });
      },

      async setSalespersonContext(salesPerson: SalespersonDTO): Promise<void> {
        try {
          patchState(store, () => ({
            isLoading: true,
            error: null,
          }));
          await lastValueFrom(userContextSalespersonService.setUserContextSalesperson(salesPerson));
          patchState(store, () => ({
            isLoading: false,
            error: null,
          }));
          await this.fetchSalespersonContext();
        } catch (error) {
          console.error(`Error while setting sales person context: ${error}`);
          patchState(store, () => ({
            isLoading: false,
            error: (error as Error)?.message || 'Failed to set salesperson context.',
          }));
        }
      },

      async fetchSalespersonContext(): Promise<void> {
        try {
          patchState(store, () => ({
            isLoading: true,
            error: null,
          }));
          const salesPerson = await lastValueFrom(userContextSalespersonService.getUserContextSalesperson());
          patchState(store, () => ({
            user: {
              ...store.user(),
              context: {
                name: salesPerson.name,
                id: salesPerson.id,
                type: UserContextType.salesperson,
              },
            } as UserDTO,
            isLoading: false,
          }));
        } catch (err) {
          console.error(`Error while fetching sales person context: ${err}`);
          patchState(store, () => ({
            isLoading: false,
            error: (err as Error)?.message || 'Failed to fetch salesperson context.',
          }));
        }
      },
    };
  }),
);
